/** Imperative CodeMirror 6 mount bound to a LoroText (I12). Language support is lazy-loaded. */
import { defaultKeymap, indentWithTab } from "@codemirror/commands";
import { StreamLanguage } from "@codemirror/language";
import { Compartment, EditorState, type Extension } from "@codemirror/state";
import { EditorView, keymap, lineNumbers } from "@codemirror/view";
import type { LoroDoc, LoroText } from "loro-crdt";
import type { CodeLanguage } from "@kss/schema";
import { loroTextBinding } from "./loroText";

export const CODE_LANGUAGES: CodeLanguage[] = ["plaintext", "python", "javascript", "typescript", "rust", "c", "cpp", "fortran", "json", "yaml", "toml", "sql", "sparql", "turtle", "bash", "markdown"];

export async function languageExtension(lang: CodeLanguage): Promise<Extension> {
  switch (lang) {
    case "python":
      return (await import("@codemirror/lang-python")).python();
    case "javascript":
      return (await import("@codemirror/lang-javascript")).javascript();
    case "typescript":
      return (await import("@codemirror/lang-javascript")).javascript({ typescript: true });
    case "rust":
      return (await import("@codemirror/lang-rust")).rust();
    case "c":
    case "cpp":
      return (await import("@codemirror/lang-cpp")).cpp();
    case "json":
      return (await import("@codemirror/lang-json")).json();
    case "yaml":
      return (await import("@codemirror/lang-yaml")).yaml();
    case "sql":
      return (await import("@codemirror/lang-sql")).sql();
    case "markdown":
      return (await import("@codemirror/lang-markdown")).markdown();
    case "fortran":
      return StreamLanguage.define((await import("@codemirror/legacy-modes/mode/fortran")).fortran);
    case "sparql":
      return StreamLanguage.define((await import("@codemirror/legacy-modes/mode/sparql")).sparql);
    case "turtle":
      return StreamLanguage.define((await import("@codemirror/legacy-modes/mode/turtle")).turtle);
    case "toml":
      return StreamLanguage.define((await import("@codemirror/legacy-modes/mode/toml")).toml);
    case "bash":
      return StreamLanguage.define((await import("@codemirror/legacy-modes/mode/shell")).shell);
    default:
      return [];
  }
}

export interface CmMount {
  view: EditorView;
  setLanguage(lang: CodeLanguage): Promise<void>;
  setReadOnly(ro: boolean): void;
}

export function mountCodeMirror(parent: HTMLElement, opts: { doc: LoroDoc; text: LoroText; readOnly: boolean; language?: CodeLanguage; lineNumbers?: boolean; onExitUp?: () => void; onExitDown?: () => void; onFocus?: () => void; extra?: Extension[] }): CmMount {
  const langC = new Compartment();
  const roC = new Compartment();
  const view = new EditorView({
    parent,
    state: EditorState.create({
      doc: opts.text.toString(),
      extensions: [
        opts.lineNumbers === false ? [] : lineNumbers(),
        keymap.of([
          {
            key: "ArrowUp",
            run: (v) => {
              if (opts.onExitUp && v.state.selection.main.head === 0) {
                opts.onExitUp();
                return true;
              }
              return false;
            },
          },
          {
            key: "ArrowDown",
            run: (v) => {
              if (opts.onExitDown && v.state.selection.main.head === v.state.doc.length) {
                opts.onExitDown();
                return true;
              }
              return false;
            },
          },
          indentWithTab,
          ...defaultKeymap.filter((b) => !["Mod-z", "Mod-y", "Mod-Shift-z"].includes(b.key ?? "")),
        ]),
        langC.of([]),
        roC.of([EditorState.readOnly.of(opts.readOnly), EditorView.editable.of(!opts.readOnly)]),
        loroTextBinding(opts.doc, opts.text),
        EditorView.domEventHandlers({ focus: () => { opts.onFocus?.(); return false; } }),
        EditorView.theme({ "&": { fontSize: "0.9rem" }, ".cm-content": { fontFamily: "ui-monospace, monospace" } }),
        ...(opts.extra ?? []),
      ],
    }),
  });
  const mount: CmMount = {
    view,
    async setLanguage(lang) {
      const ext = await languageExtension(lang);
      if (!view.dom.isConnected) return;
      view.dispatch({ effects: langC.reconfigure(ext) });
    },
    setReadOnly(ro) {
      view.dispatch({ effects: roC.reconfigure([EditorState.readOnly.of(ro), EditorView.editable.of(!ro)]) });
    },
  };
  if (opts.language) void mount.setLanguage(opts.language);
  return mount;
}
